import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Image as ImageIcon, ArrowUp, ArrowDown } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const RECOMMENDED_W = 1920;
const RECOMMENDED_H = 620;

const emptyForm = {
  image_url: "",
  image_public_id: "",
  caption: "",
  link_url: "",
  order: 0,
  status: "Active",
};

function Field({ label, hint, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
      {hint ? <div className="mt-1 text-[11px] text-[#6b655b]">{hint}</div> : null}
    </label>
  );
}

// Lightweight client-side dimension probe — purely informational
function useImageDimensions(url) {
  const [dims, setDims] = useState(null);
  useEffect(() => {
    if (!url) {
      setDims(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setDims({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => !cancelled && setDims(null);
    img.src = url;
    return () => {
      cancelled = true;
    };
  }, [url]);
  return dims;
}

export default function HeaderSlider() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/header-slides");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const visible = useMemo(
    () => items.filter((it) => (statusFilter ? it.status === statusFilter : true)),
    [items, statusFilter],
  );

  const nextOrder = useMemo(() => {
    if (items.length === 0) return 1;
    return Math.max(...items.map((x) => x.order || 0)) + 1;
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: nextOrder });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      image_url: s.image_url || "",
      image_public_id: s.image_public_id || "",
      caption: s.caption || "",
      link_url: s.link_url || "",
      order: s.order ?? 0,
      status: s.status || "Active",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.image_url) return toast.error("Please upload a slide image");
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) {
        const { data } = await api.put(`/institute/header-slides/${editing.id}`, payload);
        setItems((arr) =>
          arr
            .map((x) => (x.id === data.id ? data : x))
            .sort((a, b) => (a.order || 0) - (b.order || 0)),
        );
        toast.success("Slide updated");
      } else {
        const { data } = await api.post("/institute/header-slides", payload);
        setItems((arr) =>
          [...arr, data].sort((a, b) => (a.order || 0) - (b.order || 0)),
        );
        toast.success("Slide added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const s = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/header-slides/${s.id}`);
      setItems((arr) => arr.filter((x) => x.id !== s.id));
      toast.success("Slide removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  // Quick reorder by ±1 — swap order values
  const move = async (s, delta) => {
    const sorted = items.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex((x) => x.id === s.id);
    const swapIdx = idx + delta;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    try {
      const [r1, r2] = await Promise.all([
        api.put(`/institute/header-slides/${s.id}`, { ...s, order: other.order || 0 }),
        api.put(`/institute/header-slides/${other.id}`, { ...other, order: s.order || 0 }),
      ]);
      setItems((arr) =>
        arr
          .map((x) => (x.id === r1.data.id ? r1.data : x.id === r2.data.id ? r2.data : x))
          .sort((a, b) => (a.order || 0) - (b.order || 0)),
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reorder failed");
    }
  };

  const dims = useImageDimensions(form.image_url);
  const dimsOk = !dims || (dims.w === RECOMMENDED_W && dims.h === RECOMMENDED_H);

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="header-slider-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Website</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Header Slider</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Upload the banner images that appear on top of your institute's website. Recommended
            size <strong>1920 × 620 px</strong>, max <strong>500 KB</strong> per image. Use the
            order column to sequence the slides and toggle Active / Inactive to control visibility.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            data-testid="header-slider-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Slide
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-6 items-center">
        <select
          data-testid="header-slider-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={inputCls + " w-auto"}
        >
          <option value="">All statuses</option>
          <option value="Active">Active only</option>
          <option value="Inactive">Inactive only</option>
        </select>
        <div className="ml-auto text-sm text-[#6b655b]">
          {visible.length} of {items.length}
        </div>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : visible.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="header-slider-empty"
        >
          <ImageIcon size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">
            {items.length === 0 ? "No slides yet" : "No matches for this filter"}
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            {items.length === 0 && canEdit
              ? `Click "Add Slide" to upload the first banner.`
              : items.length === 0
                ? "Your Institute Admin hasn't added any yet."
                : "Try clearing the filter above."}
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="header-slider-list">
          {visible.map((s, i) => (
            <article
              key={s.id}
              data-testid={`header-slider-row-${s.id}`}
              className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden flex flex-col md:flex-row"
            >
              <div className="md:w-1/3 bg-[#f9f8f6] aspect-[1920/620] md:aspect-auto md:min-h-[140px] overflow-hidden">
                <img
                  src={s.image_url}
                  alt={s.caption || `Slide ${s.order}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    data-testid={`header-slider-order-${s.id}`}
                    className="inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border border-[#234333]/40 text-[#234333] bg-[#234333]/5 font-mono"
                  >
                    Order {s.order}
                  </span>
                  <span
                    className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border ${
                      s.status === "Active"
                        ? "border-[#234333]/40 text-[#234333] bg-[#234333]/5"
                        : "border-[#9e2a2b]/40 text-[#9e2a2b] bg-[#9e2a2b]/5"
                    }`}
                  >
                    {s.status}
                  </span>
                </div>
                {s.caption && (
                  <div className="font-serif text-lg text-[#1c1a17] line-clamp-2">
                    {s.caption}
                  </div>
                )}
                {s.link_url && (
                  <div className="text-[11px] text-[#6b655b] truncate">
                    Links to: <span className="font-mono">{s.link_url}</span>
                  </div>
                )}

                {canEdit && (
                  <div className="mt-auto flex flex-wrap gap-1 pt-2">
                    <button
                      onClick={() => move(s, -1)}
                      disabled={i === 0}
                      data-testid={`header-slider-up-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm border border-[#e5e0d8] disabled:opacity-40"
                    >
                      <ArrowUp size={12} strokeWidth={1.5} /> Move up
                    </button>
                    <button
                      onClick={() => move(s, +1)}
                      disabled={i === visible.length - 1}
                      data-testid={`header-slider-down-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm border border-[#e5e0d8] disabled:opacity-40"
                    >
                      <ArrowDown size={12} strokeWidth={1.5} /> Move down
                    </button>
                    <button
                      onClick={() => openEdit(s)}
                      data-testid={`header-slider-edit-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm border border-[#e5e0d8]"
                    >
                      <Pencil size={12} strokeWidth={1.5} /> Edit
                    </button>
                    <button
                      onClick={() => setConfirmDelete(s)}
                      data-testid={`header-slider-delete-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm border border-[#9e2a2b]/30"
                    >
                      <Trash2 size={12} strokeWidth={1.5} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="header-slider-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Slide" : "Add Slide"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6b655b]">
              Upload a banner image (1920 × 620 px recommended), set its order, and choose its
              status. Optional caption and click-through URL can be added.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <div className="md:col-span-2">
              <FileUploader
                kind="inst_header_slide"
                label="Slide image"
                url={form.image_url}
                testid="header-slider-image"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, image_url: url, image_public_id: public_id }))
                }
              />
              {form.image_url && dims && (
                <div
                  className={`mt-1 text-[11px] ${
                    dimsOk ? "text-[#6b655b]" : "text-[#8a6d1d]"
                  }`}
                  data-testid="header-slider-dims-hint"
                >
                  Detected dimensions: {dims.w} × {dims.h} px.{" "}
                  {dimsOk
                    ? "Matches the recommended 1920 × 620 size."
                    : `Recommended size is 1920 × 620 — this image will still be saved but may stretch or crop on the website.`}
                </div>
              )}
            </div>

            <Field
              label="Order *"
              hint="Smaller number appears first on the website."
            >
              <input
                data-testid="header-slider-order-input"
                type="number"
                min={0}
                max={9999}
                className={inputCls}
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
              />
            </Field>

            <Field label="Status *">
              <select
                data-testid="header-slider-status-select"
                className={inputCls}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>

            <Field label="Caption (optional)" full hint="Shown as overlay text. Max 200 characters.">
              <input
                data-testid="header-slider-caption-input"
                className={inputCls}
                maxLength={200}
                placeholder="e.g., Admissions open for AY 2025-26"
                value={form.caption}
                onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
              />
            </Field>

            <Field label="Click-through URL (optional)" full hint="Where the slide should link to.">
              <input
                data-testid="header-slider-link-input"
                className={inputCls}
                placeholder="https://gsmandal.org/admissions"
                value={form.link_url}
                onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))}
              />
            </Field>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="header-slider-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="header-slider-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this slide?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              The slide and its image will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="header-slider-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
